/**
 * Seam for proximity voice / video / screen share.
 *
 * Nothing here transmits anything yet. It exists so the rest of the client can
 * already ask "who should I be able to hear?" in one place: the answer is a
 * function of zone and distance, which is exactly the routing the chat server
 * already does. When a provider (LiveKit, Daily, raw WebRTC) is added, it
 * implements MediaProvider and the game loop keeps calling `syncPeers`.
 */

import { PROXIMITY_RADIUS, zoneById, type Capabilities } from '@virtual-office/shared';

import type { Store } from './state.js';

export interface PeerTarget {
  playerId: string;
  /** 0 = silent, 1 = full volume. Fades out towards the edge of earshot. */
  volume: number;
}

export interface MediaProvider {
  readonly kind: string;
  /** Called once capabilities are known. */
  init(capabilities: Capabilities): Promise<void>;
  /** Called from the game loop with who should be audible and how loudly. */
  syncPeers(targets: PeerTarget[]): void;
  destroy(): void;
}

/**
 * Who the local player should hear right now.
 *
 * In a closed room (audio.mode === 'zone') everyone inside is at full volume.
 * Everywhere else, volume falls off with distance.
 */
export function audiblePeers(store: Store): PeerTarget[] {
  const me = store.me;
  const map = store.map;
  if (!me || !map) return [];

  const zone = zoneById(map, me.zoneId);
  const targets: PeerTarget[] = [];

  for (const other of store.others) {
    if (zone && zone.audio.mode === 'zone') {
      if (other.zoneId === me.zoneId) targets.push({ playerId: other.id, volume: 1 });
      continue;
    }
    const distance = Math.hypot(other.x - me.x, other.y - me.y);
    if (distance > PROXIMITY_RADIUS) continue;
    // Linear falloff over the outer third of the bubble.
    const volume = Math.min(1, (PROXIMITY_RADIUS - distance) / (PROXIMITY_RADIUS / 3));
    targets.push({ playerId: other.id, volume: Math.max(0, volume) });
  }
  return targets;
}

/** Used until a real provider is configured; keeps the call sites honest. */
export class NoopMediaProvider implements MediaProvider {
  readonly kind = 'none';
  async init(): Promise<void> {}
  syncPeers(): void {}
  destroy(): void {}
}

export function createMediaProvider(capabilities: Capabilities): MediaProvider {
  if (!capabilities.voice && !capabilities.video) return new NoopMediaProvider();
  // A real provider slots in here once the server hands out media tokens.
  console.info('[media] server advertises voice, but no client provider is built yet');
  return new NoopMediaProvider();
}
